import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { CircleCheck } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Button } from '@/components/ui/button';
import { MODAL_TIMEOUT } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useCountdown } from '@/hooks/useCountdown';
import { useSocket } from '@/hooks/useSocket';
import { useWidgetState } from '@/hooks/useWidgetState';
import { chatModalState, isWidgetExpandedState, sessionState, shouldDisplaySurveySelector, lastBrainResponseDefaultSurveySelector, afterSurveyAction, } from '@/state/session';
import { formatMinutesSecondsMMSS, sleep } from '@/util/utils';
export const Resolved = () => {
    const { t } = useTranslation();
    const { trackEvent } = useAnalytics();
    const { closeWidget } = useWidgetState();
    const { reopenConversation, closeSession } = useSocket();
    const [session, setSessionState] = useRecoilState(sessionState);
    const keep_alive = session === null || session === void 0 ? void 0 : session.keep_alive;
    const remaining_keep_alive = session === null || session === void 0 ? void 0 : session.remaining_keep_alive;
    const { minutes, seconds } = useCountdown(remaining_keep_alive !== null && remaining_keep_alive !== void 0 ? remaining_keep_alive : keep_alive);
    const shouldDisplaySurvey = useRecoilValue(shouldDisplaySurveySelector);
    const hasResponseSurvey = useRecoilValue(lastBrainResponseDefaultSurveySelector);
    const setChatModal = useSetRecoilState(chatModalState);
    const isExpanded = useRecoilValue(isWidgetExpandedState);
    const setAfterSurveyAction = useSetRecoilState(afterSurveyAction);
    const handleClose = useCallback(async () => {
        setChatModal(null);
        trackEvent(AnalyticsEvent.PopoverResolvedEndChatClicked);
        const clickAction = () => {
            closeSession();
            closeWidget();
        };
        await sleep(MODAL_TIMEOUT);
        if (shouldDisplaySurvey) {
            setAfterSurveyAction(() => clickAction);
            setChatModal("survey" /* ChatModal.Survey */);
        }
        else {
            clickAction();
        }
    }, [
        setChatModal,
        trackEvent,
        shouldDisplaySurvey,
        closeSession,
        closeWidget,
        setAfterSurveyAction,
    ]);
    const handleCancel = useCallback(async () => {
        trackEvent(AnalyticsEvent.PopoverResolvedContinueClicked);
        // Show survey only if there's a survey action from the response
        if (hasResponseSurvey) {
            setChatModal(null);
            const clickAction = () => {
                reopenConversation();
            };
            await sleep(MODAL_TIMEOUT);
            setAfterSurveyAction(() => clickAction);
            setChatModal("survey" /* ChatModal.Survey */);
        }
        else {
            reopenConversation();
            setChatModal(null);
        }
    }, [
        reopenConversation,
        setChatModal,
        trackEvent,
        hasResponseSurvey,
        setAfterSurveyAction,
    ]);
    useEffect(() => {
        (async () => {
            if (minutes === 0 && seconds === 0) {
                setChatModal(null);
                await sleep(MODAL_TIMEOUT);
                setSessionState((prev) => (Object.assign(Object.assign({}, prev), { status: "expired" /* SessionStatus.Expired */ })));
                setChatModal("expired" /* ChatModal.Expired */);
            }
        })();
    }, [minutes, seconds, setChatModal, setSessionState]);
    const [formattedMinutes, formattedSeconds] = formatMinutesSecondsMMSS(minutes, seconds).split(':');
    return (_jsxs("div", { className: "flex h-full flex-col items-center gap-4 p-4 text-center", children: [_jsx(CircleCheck, { size: 40, color: "var(--color-successful)" }), _jsxs("div", { className: "text-lg font-semibold leading-none text-foreground", children: [t('events.resolved.title'), _jsx("p", { className: "text-foreground-muted mx-auto mt-1 text-sm font-normal leading-5", children: t('modal.resolved_continue') })] }), _jsx("div", { className: "flex w-full flex-col items-center justify-center", children: _jsxs("div", { className: "grid min-h-8 w-fit grid-cols-[36px_16px_36px] items-center", children: [_jsx("span", { className: "text-foreground-muted text-2xl leading-8", children: formattedMinutes }), _jsx("span", { className: "text-foreground-muted -translate-y-0.5 text-2xl leading-8", children: ":" }), _jsx("span", { className: "text-foreground-muted text-2xl leading-8", children: formattedSeconds })] }) }), _jsxs("div", { className: "flex w-full flex-col items-center justify-center gap-2", children: [_jsx(Button, { onClick: handleClose, className: cn('w-full', {
                            'w-sm': isExpanded,
                        }), children: t('buttons.end_conversation') }), _jsx(Button, { onClick: handleCancel, variant: "secondary", className: cn('w-full', {
                            'w-sm': isExpanded,
                        }), children: t('buttons.resume') })] })] }));
};
